import { Link } from "react-router-dom";

import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronRight,
  ICON_STROKE,
} from "@/shared/components/icons";
import type { SkillState } from "@/shared/models";
import { barFill, clampLevel } from "@/features/skills/lib/skillStats";

/** A clickable skill row — used in the summary strip (compact) and inside
 *  expanded category sections. Routes to /skills/:skillId. */
export function ClickableSkillRow({
  skill,
  compact,
}: {
  skill: SkillState;
  compact?: boolean;
}) {
  const unmapped = skill.signalCount === 0;
  const level = clampLevel(skill.level);
  return (
    <Link
      to={`/skills/${skill.skillId}`}
      className={[
        "block rounded-lg transition group",
        compact ? "p-1" : "p-2 hover:bg-text/5",
      ].join(" ")}
    >
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <span className="text-sm text-text group-hover:text-accent transition truncate">
              {skill.name}
            </span>
            <span className="text-xs text-text-muted flex items-center gap-1.5 flex-shrink-0">
              {!unmapped && <TrendArrow trend={skill.trend} />}
              <span>{unmapped ? "–" : level}</span>
              {!unmapped && (
                <span className="text-text-muted/60 hidden sm:inline">
                  · {skill.signalCount}
                </span>
              )}
            </span>
          </div>
          <div className="h-1 bg-border rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${Math.max(2, level)}%`, backgroundColor: barFill(level, unmapped) }}
            />
          </div>
        </div>
        <ChevronRight
          size={14}
          strokeWidth={ICON_STROKE}
          className="text-text-muted/60 group-hover:text-accent transition flex-shrink-0"
        />
      </div>
    </Link>
  );
}

export function TrendArrow({ trend }: { trend: SkillState["trend"] }) {
  if (trend === "steady") return null;
  const Icon = trend === "up" ? ArrowUpRight : ArrowDownRight;
  const cls = trend === "up" ? "text-green-400/80" : "text-red-400/80";
  return <Icon size={12} strokeWidth={2} className={cls} />;
}
